/**
 * Runtime configuration, fetched rather than compiled in.
 *
 * The widget is a whitelabel artefact: one image, installed into infrastructure we cannot see, framed by an
 * origin we do not know at build time. Baking `VITE_` values in would mean a customer-specific image per
 * deployment, and every value here is one that differs per customer - which origins may frame us, where the
 * API lives, and how hard the panel is allowed to poll it.
 *
 * So nginx writes `/config.json` from environment variables when the container starts, using the same
 * envsubst pass that writes the `frame-ancestors` policy. One mechanism, two files, and they cannot drift
 * apart into a widget that trusts an origin the CSP forbids.
 *
 * The fallback is development only, and it is a fallback rather than a default: a deployed container always
 * has the file, because the entrypoint writes it before nginx serves anything.
 */
import { z } from 'zod';

/**
 * Fifteen seconds, the same number `frontend/ui/src/config.ts` exports. `0` turns polling off (DG.24).
 *
 * Exported so a caller and a test name the default rather than repeating the literal, and identical to the
 * standalone application's on purpose: two surfaces reading one environment variable must not disagree
 * about what an unset one means.
 */
export const DEFAULT_POLL_SECONDS = 15;
export const DEFAULT_DOCUMENT_REQUEST_MAX_LENGTH = 2000;

/**
 * Let a real number or a non-blank string through to the coercion, and turn everything else into
 * `undefined` so the schema's `catch` supplies the default.
 *
 * Three peer cases as three statements rather than one expression, because the interesting one is the
 * middle: the entrypoint writes every scalar quoted, so an unset variable arrives as `""` - and
 * `Number("")` is `0`, which is a *valid* interval here meaning "never poll". Blanking it out is what
 * stops an unconfigured deployment reading as a deliberate instruction to switch polling off.
 */
function numericOrAbsent(value: unknown): unknown {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') return value;
  return undefined;
}

const configSchema = z.object({
  /** Where `/api/v1` lives. Absolute, because the widget is on its own origin. */
  apiBaseUrl: z.string().min(1),
  /**
   * Origins allowed to frame this widget and exchange messages with it.
   *
   * Exact origins, no wildcards - section 14 forbids posting to `"*"` and the same reasoning applies to
   * accepting from one. An empty list means the widget refuses every handshake, which is the correct
   * failure for an unconfigured container: it fails closed, and the `frame-ancestors` policy written by the
   * same pass says `'none'`, so it will not even load.
   */
  hostOrigins: z.array(z.string().min(1)),
  /**
   * How often the panel re-reads the exchange, in seconds. `0` turns polling off (DG.24).
   *
   * Character for character the same schema as the standalone application's, which is the point: both
   * surfaces are configured from one environment variable by one entrypoint, so they must not disagree
   * about what a blank or a mistyped one means.
   *
   * `catch` because this schema is all-or-nothing. A value that failed to parse would take `hostOrigins`
   * down with it and return the whole development fallback, whose allowlist names localhost only - so a
   * panel inside a real CRM would refuse its own host and tell a representative it was "not given access
   * by the page containing it", which is a sentence about origins for a typo about time.
   *
   * The `preprocess` is not ceremony around the coercion, it is the whole correctness of it; see
   * `numericOrAbsent` for the empty-string trap it closes. `null` and `[]` coerce to `0` the same way.
   *
   * The upper bound is not decoration either: `setInterval` above 2^31-1 milliseconds overflows and fires
   * immediately, so an absurd value polls continuously rather than never.
   */
  pollSeconds: z.preprocess(numericOrAbsent, z.coerce.number().int().min(0).max(86_400)).catch(DEFAULT_POLL_SECONDS),
  /** Maximum length of a rep's authored formatted document request. */
  documentRequestMaxLength: z.preprocess(numericOrAbsent, z.coerce.number().int().min(100).max(50_000)).catch(DEFAULT_DOCUMENT_REQUEST_MAX_LENGTH),
});

export type WidgetConfig = z.infer<typeof configSchema>;

export function devFallback(): WidgetConfig {
  return {
    apiBaseUrl: 'http://localhost:3000',
    hostOrigins: ['http://localhost:8080', 'http://127.0.0.1:8080'],
    pollSeconds: DEFAULT_POLL_SECONDS,
    documentRequestMaxLength: DEFAULT_DOCUMENT_REQUEST_MAX_LENGTH,
  };
}

export async function loadConfig(): Promise<WidgetConfig> {
  try {
    const response = await fetch('/config.json', { cache: 'no-store' });
    if (!response.ok) return devFallback();
    const parsed = configSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : devFallback();
  } catch {
    // No file in `vite dev`, which serves `public/` and has none. Every other failure - malformed JSON, a
    // missing key - lands here too and is treated the same way, because the alternative is a blank panel
    // with an explanation only in a console nobody has open.
    return devFallback();
  }
}

export const runtimeConfig: WidgetConfig = await loadConfig();
